import { useState, useMemo } from 'react';

const KIND_META = {
  plan: { icon: '💳', label: 'Hiring plan', tint: 'from-[#EDE9FE] to-[#FCE7F3]' },
  hiring_pack: { icon: '📦', label: 'Hiring pack', tint: 'from-[#EDE9FE] to-[#FCE7F3]' },
  job_product: { icon: '📝', label: 'Job post', tint: 'from-[#EDE9FE] to-[#FCE7F3]' },
  credit_topup: { icon: '👤', label: 'Candidate credits', tint: 'from-[#E0F2FE] to-[#EDE9FE]' },
  job_boost: { icon: '🚀', label: 'Job boost', tint: 'from-[#FCE7F3] to-[#FEF3C7]' },
  featured_job: { icon: '🌟', label: 'Featured job', tint: 'from-[#FEF3C7] to-[#FCE7F3]' },
};

const metaFor = (kind) =>
  KIND_META[kind] || { icon: '🧾', label: 'Payment', tint: 'from-[#EDE9FE] to-[#FCE7F3]' };

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const fmtDate = (d) =>
  new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const fmtDateTime = (d) =>
  new Date(d).toLocaleString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });

function StatusPill({ status }) {
  const paid = (status || 'paid').toLowerCase() === 'paid';
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${
        paid ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${paid ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      {paid ? 'Paid' : status}
    </span>
  );
}

function ReceiptModal({ receipt, onClose }) {
  const meta = metaFor(receipt.kind);
  const rows = [
    ['Item', receipt.item_name],
    receipt.billing_cycle && ['Billing', receipt.billing_cycle.charAt(0).toUpperCase() + receipt.billing_cycle.slice(1)],
    receipt.validity_text && ['Validity', receipt.validity_text],
    ['Date', fmtDateTime(receipt.created_at)],
    receipt.gateway && ['Paid via', receipt.gateway.charAt(0).toUpperCase() + receipt.gateway.slice(1)],
    ['Order ID', receipt.payment_reference],
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#1E0F3D]/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #pb-receipt, #pb-receipt * { visibility: visible !important; }
          #pb-receipt { position: fixed; left: 0; top: 0; width: 100%; box-shadow: none !important; }
          .pb-no-print { display: none !important; }
        }
      `}</style>

      <div
        id="pb-receipt"
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-gradient-to-br from-[#6D28D9] via-[#9333EA] to-[#DB2777] text-white px-6 pt-7 pb-9 text-center">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -left-10 -bottom-12 w-32 h-32 rounded-full bg-white/10" />
          <div className="relative mx-auto w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl mb-3">
            ✓
          </div>
          <p className="relative text-sm text-white/85">Payment received</p>
          <p className="relative text-4xl font-extrabold mt-1">{inr(receipt.amount)}</p>
          <p className="relative text-xs text-white/75 mt-2">Receipt {receipt.receipt_no}</p>
        </div>

        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-4">
            <span className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.tint} flex items-center justify-center text-xl`}>
              {meta.icon}
            </span>
            <div className="min-w-0">
              <p className="font-bold text-[#1E0F3D] truncate">{receipt.item_name}</p>
              <p className="text-xs text-gray-500">{meta.label}</p>
            </div>
            <div className="ml-auto"><StatusPill status={receipt.status} /></div>
          </div>

          <dl className="text-sm">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-2.5 border-t border-[#F3E8FF]">
                <dt className="text-gray-500 shrink-0">{k}</dt>
                <dd className="font-semibold text-[#1E0F3D] text-right break-all">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="pb-no-print flex gap-3 mt-5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl text-sm font-bold border-2 border-[#F3E8FF] text-gray-500 hover:bg-[#F5F0FA] transition"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="flex-1 py-3 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-[#7C3AED] to-[#DB2777] hover:shadow-lg transition"
            >
              Print receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BillingHistory({ records = [] }) {
  const [receipt, setReceipt] = useState(null);

  const totalPaid = useMemo(
    () => records.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
    [records]
  );

  return (
    <section className="bg-white rounded-3xl shadow-lg border border-[#F3E8FF] overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-4 px-5 sm:px-7 py-5 bg-gradient-to-r from-[#F5F0FA] to-white border-b border-[#F3E8FF]">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white text-xl flex items-center justify-center shadow-md">
            🧾
          </span>
          <div>
            <h3 className="font-extrabold text-[#1E0F3D] text-lg leading-tight">Billing history</h3>
            <p className="text-xs text-gray-500">Your payments and receipts</p>
          </div>
        </div>

        {records.length > 0 && (
          <div className="flex gap-3">
            <div className="bg-white border border-[#F3E8FF] rounded-xl px-4 py-2 text-right">
              <p className="text-[11px] text-gray-500">Total paid</p>
              <p className="font-extrabold text-[#7C3AED] leading-tight">{inr(totalPaid)}</p>
            </div>
            <div className="bg-white border border-[#F3E8FF] rounded-xl px-4 py-2 text-right">
              <p className="text-[11px] text-gray-500">Payments</p>
              <p className="font-extrabold text-[#1E0F3D] leading-tight">{records.length}</p>
            </div>
          </div>
        )}
      </div>

      {records.length === 0 ? (
        <div className="text-center px-6 py-14">
          <div className="mx-auto w-16 h-16 rounded-full bg-[#F5F0FA] flex items-center justify-center text-3xl mb-4">
            🧾
          </div>
          <p className="font-bold text-[#1E0F3D]">No payments yet</p>
          <p className="text-sm text-gray-500 mt-1">
            When you buy a plan or add-on, your receipt will show up here.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-[#F3E8FF]">
          {records.map((r) => {
            const meta = metaFor(r.kind);
            return (
              <li
                key={r.id}
                className="group flex items-center gap-4 px-5 sm:px-7 py-4 hover:bg-[#FBF8FF] transition-colors"
              >
                <span
                  className={`w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br ${meta.tint} flex items-center justify-center text-2xl`}
                >
                  {meta.icon}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="font-bold text-[#1E0F3D] truncate">{r.item_name}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {meta.label}
                    {r.billing_cycle ? ` (${r.billing_cycle})` : ''} · {fmtDate(r.created_at)}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5 truncate">{r.receipt_no}</p>
                </div>

                <div className="hidden sm:block"><StatusPill status={r.status} /></div>

                <div className="text-right shrink-0">
                  <p className="font-extrabold text-[#1E0F3D]">{inr(r.amount)}</p>
                  <button
                    type="button"
                    onClick={() => setReceipt(r)}
                    className="mt-1 text-xs font-bold text-[#7C3AED] hover:text-[#DB2777] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C3AED] rounded"
                  >
                    View receipt
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {receipt && <ReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />}
    </section>
  );
}